package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage

/** The steps of the Add / Claim business wizard (intro first, then the 5 numbered steps). */
enum class WizardStep { Intro, Info, Location, Photos, Products, Plan }

/**
 * The subscription tiers shown on the final step. Until payments launch (Phase 6), only [Free] is
 * selectable — paid tiers are "coming soon" **interest capture** (`docs/ROADMAP.md` monetization
 * decision: no fake paywalls; interest feeds founding-business pricing outreach).
 */
enum class BusinessPlan { Free, Standard, Premium }

/**
 * The full draft accumulated across the wizard. Name/description are [LocalizedText] so owners can
 * author EN/FR/FA per `docs/LOCALIZATION.md`. Media/document fields hold picked-file references
 * (upload to Storage is Phase 2). Finishing the wizard persists the draft as a pending
 * `businesses/{id}` doc (see `AddBusinessViewModel.submitFree`).
 */
data class AddBusinessDraft(
    // Step 1 — Info
    val name: LocalizedText = LocalizedText(),
    val categoryId: String? = null,
    val description: LocalizedText = LocalizedText(),
    /** Enabled structured-fact keys for the picked category (BusinessAttributeRegistry). */
    val attributes: Set<String> = emptySet(),
    // Step 2 — Location
    val addressLine: String = "",
    val city: String = "",
    val province: String = "",
    val postalCode: String = "",
    // Step 3 — Photos & Verification. Picked on-device; uploaded to Storage after the pending
    // `businesses/{id}` doc exists (its ownerUid is what storage.rules authorizes against).
    val cover: PickedImage? = null,
    val logo: PickedImage? = null,
    val gallery: List<PickedImage> = emptyList(),
    val documentRef: String? = null,
    // Step 4 — Products & promotions (OPTIONAL — many businesses have none; Next skips freely).
    // Written to `businesses/{id}/products/*` after submit; editable later via Dashboard.
    val products: List<DraftProduct> = emptyList(),
    // Step 4 — Plan. `plan` is what the listing actually gets (Free until payments exist);
    // `paidPlanInterest` records which paid tier the owner asked to be notified about.
    val plan: BusinessPlan? = null,
    val paidPlanInterest: BusinessPlan? = null,
) {
    val isInfoValid: Boolean
        get() = !name.isEmpty && categoryId != null && !description.isEmpty

    val isLocationValid: Boolean
        get() = addressLine.isNotBlank() && city.isNotBlank() && province.isNotBlank()

    /** Gallery slots shown on the Photos step (and the upload cap). */
    companion object { const val MAX_GALLERY_PHOTOS = 3 }
}

/**
 * A product drafted in the wizard's optional Step 4. [promoted] marks it as the business's
 * promotional/featured product (rendered in the Business Detail promotions rail). Prices are
 * integer cents (CAD) — same contract as [diana.soleil.design.iraniancanadienbazzar.core.model.Product].
 */
data class DraftProduct(
    val name: String,
    val priceCents: Long,
    val promoted: Boolean = false,
)

/** Returns a copy of this [LocalizedText] with [language]'s value replaced by [value]. */
fun LocalizedText.with(language: Language, value: String): LocalizedText = when (language) {
    Language.EN -> copy(en = value)
    Language.FR -> copy(fr = value)
    Language.FA -> copy(fa = value)
}
